import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { supabase } from "../supabaseClient.js";

// Sidebar/Navbar imports removed
import { MEMORY_CACHE } from '../services/GlobalCache.js';
import CacheService from '../services/CacheService.js';

// Edge Functions
const EDGE_PAYMENT_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/payment-create`;
const EDGE_FREE_CLAIM_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/payment-free-claim`;
const EDGE_GET_METHODS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/payment-methods`;

/* ---------- util ---------- */
const rupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

/* filter groups */
const GROUPS = [
  { key: "QR", labelKey: "qr" },
  { key: "VA", labelKey: "va" },
  { key: "EW", labelKey: "ew" },
  { key: "CC", labelKey: "cc" },
  { key: "EB", labelKey: "eb" },
  { key: "ALL", labelKey: "all" },
];
const codeToGroup = (code = "") => {
  const c = code.toUpperCase();
  if (["VC"].includes(c)) return "CC";
  if (["JP"].includes(c)) return "EB";
  if (["SP", "NQ", "GQ", "SQ"].includes(c)) return "QR";
  if (["OV", "SA", "DA", "LA", "OL", "SL"].includes(c)) return "EW";
  return "VA";
};

export default function TopupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setPageTitle, profile: ctxProfile } = useOutletContext();

  useEffect(() => {
    setPageTitle(t('topup.pageTitle'));
  }, [setPageTitle, t]);

  // base data
  const [user, setUser] = useState(null);

  // Init profile from Cache to prevent flashing
  const [profile, setProfile] = useState(() => ctxProfile || CacheService.get('user_profile') || null);

  const [userFeatures, setUserFeatures] = useState([]);
  const [sidebarItemsDb, setSidebarItemsDb] = useState([]);

  // packages
  const [packages, setPackages] = useState([]);
  const [packageFeatures, setPackageFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  // checkout modal
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // voucher
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherInfo, setVoucherInfo] = useState(null);
  const [voucherStatus, setVoucherStatus] = useState("idle");
  const [defaultCommissionPercent, setDefaultCommissionPercent] = useState(10);

  // channels
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [selectedPayMethod, setSelectedPayMethod] = useState("");
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("QR");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [claimedPackageIds, setClaimedPackageIds] = useState([]); // Track claimed packages

  /* ---------- bootstrap ---------- */
  useEffect(() => {
    const run = async () => {
      try {


        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;
        if (authError || !authUser) { navigate("/login"); return; }
        setUser(authUser);

        const { data: profileRow, error: profileError } = await supabase
          .from("profiles").select("*").eq("id", authUser.id).single();
        if (profileError && profileError.code !== "PGRST116") throw profileError;
        if (!profileRow) { navigate("/login"); return; }

        setProfile(profileRow);
        CacheService.set('user_profile', profileRow, 60);

        const { data: fRows } = await supabase
          .from("user_features").select("user_id,feature_code").eq("user_id", authUser.id);
        setUserFeatures((fRows || []).map(r => r.feature_code));

        // Always fetch fresh data in background (or foreground if no cache)
        const { data: pkgRows } = await supabase
          .from("credit_packages")
          .select("id,name,price,original_price,base_credits,populer,is_custom_button,button_text,button_url,custom_features")
          .eq("is_active", true).order("price", { ascending: true });

        setPackages(pkgRows || []);

        // Update caches
        if (pkgRows) {
          MEMORY_CACHE['__GENERAL_PACKAGES__'] = pkgRows;
          CacheService.set('__GENERAL_PACKAGES__', pkgRows);
        }

        if (pkgRows?.length) {
          const { data: pfRows } = await supabase
            .from("package_features")
            .select("package_id, feature:app_features(slug,name,description)")
            .in("package_id", pkgRows.map(p => p.id));
          const map = {};
          (pfRows || []).forEach(r => {
            if (!map[r.package_id]) map[r.package_id] = [];
            if (r.feature) {
              // normalize slug to code for frontend compatibility
              const f = { ...r.feature, code: r.feature.slug };
              map[r.package_id].push(f);
            }
          });
          setPackageFeatures(map);
        }

        let cp = 10;
        const { data: setRow } = await supabase
          .from("app_settings").select("key,value").eq("key", "affiliate_commission_percent").single();
        if (setRow?.value != null) cp = Number(setRow.value);
        setDefaultCommissionPercent(cp);

        // Fetch claimed packages
        if (authUser) {
          const { data: claims } = await supabase
            .from("transactions")
            .select("package_id")
            .eq("user_id", authUser.id)
            .eq("status", "paid")
            .eq("duitku_reference", "free_claim");
          if (claims && claims.length > 0) {
            setClaimedPackageIds(claims.map(c => c.package_id));
          }
        }
      } finally { setLoading(false); }
    };
    run();
  }, [navigate]);

  /* ---------- open/close modal ---------- */
  const openCheckoutModal = async (pkg) => {
    setSelectedPackage(pkg);
    setSelectedPayMethod("");
    setVoucherCode(""); setVoucherInfo(null); setVoucherStatus("idle");
    setCheckoutError(""); setIsCheckoutOpen(true);
    setSearch(""); setActiveGroup("QR");

    try {
      // Check if Free Plan
      if (Number(pkg.price) === 0) {
        // Skip fetching payment methods
        return;
      }

      // CACHE-FIRST: Payment Methods
      const cachedMethods = MEMORY_CACHE['__GENERAL_PAYMENT_METHODS__'] || CacheService.get('__GENERAL_PAYMENT_METHODS__');
      if (cachedMethods && Array.isArray(cachedMethods.methods)) {
        setChannels(cachedMethods.methods.map(m => ({
          code: m.paymentMethod,
          name: m.paymentName,
          img: m.paymentImage,
          fee: m.totalFee, // Note: Fee might be outdated if price changed, but good enough for instant load
          group: codeToGroup(m.paymentMethod),
        })));
        setChannelsLoading(false);
      } else {
        setChannelsLoading(true); setChannels([]);
      }

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(EDGE_GET_METHODS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session && { Authorization: `Bearer ${session.access_token}` }),
        },
        body: JSON.stringify({ amount: Math.round(Number(pkg.price) || 0) }),
      });
      const out = await res.json();
      if (res.ok && Array.isArray(out?.methods)) {
        // Update Cache
        MEMORY_CACHE['__GENERAL_PAYMENT_METHODS__'] = out;
        CacheService.set('__GENERAL_PAYMENT_METHODS__', out);

        setChannels(out.methods.map(m => ({
          code: m.paymentMethod,
          name: m.paymentName,
          img: m.paymentImage,
          fee: m.totalFee,
          group: codeToGroup(m.paymentMethod),
        })));
      } else { console.error("get methods failed", out); }
    } catch (e) { console.error("get methods error", e); }
    finally { setChannelsLoading(false); }
  };
  const closeCheckoutModal = () => {
    if (isSubmitting) return;
    setIsCheckoutOpen(false); setSelectedPackage(null); setChannels([]);
  };

  /* ---------- voucher live check ---------- */
  useEffect(() => {
    if (!isCheckoutOpen || !selectedPackage) return;
    const code = voucherCode.trim();
    if (!code) { setVoucherStatus("idle"); setVoucherInfo(null); return; }

    let cancel = false;
    setVoucherStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data: v, error } = await supabase
          .from("vouchers")
          .select("id,code,affiliator_id,commission_percent,is_active")
          .eq("code", code).eq("is_active", true).single();
        if (cancel) return;
        if (error || !v) { setVoucherStatus("invalid"); setVoucherInfo(null); return; }

        // 🛡️ Prevent using own voucher
        if (user && v.affiliator_id === user.id) {
          setVoucherStatus("own_voucher");
          setVoucherInfo(null);
          return;
        }

        const rate = 10, original = Number(selectedPackage.price);
        const disc = Math.round((original * rate) / 100);
        const final = original - disc;
        const cp = v.commission_percent != null ? Number(v.commission_percent) : defaultCommissionPercent;
        setVoucherInfo({ voucher: v, discountRate: rate, discountAmount: disc, finalPrice: final, commissionPercent: cp });
        setVoucherStatus("valid");
      } catch { if (!cancel) { setVoucherStatus("error"); setVoucherInfo(null); } }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [voucherCode, isCheckoutOpen, selectedPackage, defaultCommissionPercent, user]);

  /* ---------- filter/search ---------- */
  const visibleGroups = useMemo(() => {
    // If loading or empty, show standard groups (or handle differently)
    // But here we want to HIDE empty groups.
    if (!channels || channels.length === 0) return GROUPS; // Fallback or empty

    const present = new Set(channels.map(c => c.group));
    return GROUPS.filter(g => g.key === "ALL" || present.has(g.key));
  }, [channels]);

  // Auto-switch activeGroup if it becomes invalid
  useEffect(() => {
    if (channelsLoading) return;
    if (channels.length === 0) return;

    // If activeGroup is not in visibleGroups, switch to first available (usually 'QR' or 'ALL')
    // We check if the current activeGroup is in the visible list
    const isVisible = visibleGroups.some(g => g.key === activeGroup);
    if (!isVisible) {
      // Prefer 'QR' if available, else 'ALL'
      const hasQR = visibleGroups.some(g => g.key === "QR");
      setActiveGroup(hasQR ? "QR" : "ALL");
    }
  }, [channels, channelsLoading, activeGroup, visibleGroups]);

  const filteredChannels = useMemo(() => {
    let list = channels;
    if (activeGroup !== "ALL") list = list.filter(c => c.group === activeGroup);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [channels, activeGroup, search]);

  /* ---------- confirm ---------- */
  const handleConfirmPurchase = async () => {
    if (!user || !selectedPackage || isSubmitting) return;

    // FREE CLAIM LOGIC
    const isFree = Number(selectedPackage.price) === 0;

    if (!isFree && !selectedPayMethod) { setCheckoutError(t('topup.modal.errorSelectMethod')); return; }

    try {
      setIsSubmitting(true); setCheckoutError("");
      const { data: { session }, error: se } = await supabase.auth.getSession();
      if (se || !session) { setCheckoutError(t('topup.modal.errorSession')); setIsSubmitting(false); return; }

      let res, data;

      if (isFree) {
        // Call FREE CLAIM Endpoint
        res = await fetch(EDGE_FREE_CLAIM_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify({ packageId: selectedPackage.id }),
        });

        data = await res.json();

        if (!res.ok) {
          if (data.error === "Anda sudah pernah mengklaim paket ini. Paket free hanya bisa diklaim satu kali." || data.error === "CLAIM_ALREADY_EXISTS") {
            setCheckoutError(t('topup.modal.errorClaimedAlready'));
          } else {
            setCheckoutError(data.error || t('topup.modal.errorClaim'));
          }
          setIsSubmitting(false);
          return;
        }

        // Success for Free Plan -> Refresh Page / Show Success Alert
        alert(t('topup.modal.success'));
        window.location.reload();
        return;

      } else {
        // REGULAR PAYMENT
        const payload = {
          packageId: selectedPackage.id,
          voucherCode: (voucherInfo && voucherStatus === "valid") ? voucherInfo.voucher.code : (voucherCode.trim() || null),
          paymentMethod: selectedPayMethod,
        };
        res = await fetch(EDGE_PAYMENT_FUNCTION_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
          body: JSON.stringify(payload),
        });

        if (!res.ok) { setCheckoutError(t('topup.modal.errorTransaction')); setIsSubmitting(false); return; }

        data = await res.json();
        if (data.paymentUrl) window.location.href = data.paymentUrl;
        else { setCheckoutError(t('topup.modal.errorNoUrl')); setIsSubmitting(false); }
      }

    } catch (e) {
      console.error(e);
      setCheckoutError(t('topup.modal.errorGeneral'));
      setIsSubmitting(false);
    }
  };

  /* ---------- UI ---------- */
  if (loading) {
    return (
      <div className="h-screen w-full bg-slate-50 p-6 flex flex-col space-y-8 animate-pulse">
        <div className="w-full h-12 bg-slate-200 rounded-lg"></div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div key={i} className="h-72 bg-slate-200 rounded-xl"></div>)}
        </div>
      </div>
    );
  }

  const displayName = profile?.username || user?.email?.split("@")[0] || "User";
  const creditBalance = profile?.credit_balance ?? 0;
  const walletBalance = profile?.wallet_balance ?? 0;

  const defaultSidebarItems = [
    { key: "home", label: "Home", route_path: "/dashboard", is_active: true, feature_code: null },
    { key: "affiliator", label: "Affiliator", route_path: "/dashboard/affiliator", is_active: true, feature_code: null },
    { key: "topup", label: "Topup Kredit", route_path: "/dashboard/topup", is_active: true, feature_code: null },
    {
      key: "history-generate",
      label: "Riwayat Generate",
      route_path: "/dashboard/riwayat-generate",
      is_active: true,
      feature_code: null,
    },
  ];
  const allSidebarItems = [...defaultSidebarItems, ...sidebarItemsDb];

  return (
    <main className="h-full w-full overflow-y-auto p-4 lg:p-6 text-slate-900 bg-slate-50">
      <div className="max-w-7xl mx-auto grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {packages.length ? packages.map(pkg => {
          const feats = packageFeatures[pkg.id] || [];
          const pop = pkg.populer === true;
          return (
            <div key={pkg.id}
              className={`relative rounded-xl border bg-white p-4 flex flex-col transition ${pop ? "border-amber-400 shadow-md shadow-amber-100 bg-gradient-to-b from-amber-50 to-white hover:-translate-y-0.5"
                : "border-slate-200 shadow-sm hover:shadow hover:-translate-y-0.5"
                }`}>
              {pop && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-amber-400 text-[9px] font-bold text-amber-900 shadow">{t('topup.popular')}</div>}
              <div className="mb-2">
                <h2 className="text-sm font-bold text-slate-900 leading-tight">{pkg.name}</h2>
                {!pkg.is_custom_button && (
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    <Trans i18nKey="topup.getCredits" count={pkg.base_credits?.toLocaleString("id-ID")}>
                      <span className="font-semibold text-slate-700" />
                    </Trans>
                  </p>
                )}
              </div>
              <div className="mb-2">
                {pkg.original_price && Number(pkg.original_price) > Number(pkg.price) && (
                  <div className="text-[10px] text-slate-400 font-medium line-through">
                    {rupiah(pkg.original_price)}
                  </div>
                )}
                <span className="text-lg font-extrabold text-slate-900">{Number(pkg.price) === 0 ? t('topup.free') : rupiah(pkg.price)}</span>
                {Number(pkg.price) === 0 && <span className="text-[9px] text-slate-400 ml-0.5">{t('topup.perClaim')}</span>}
              </div>
              <ul className="mb-3 space-y-1 text-[10px] flex-1">
                {pkg.is_custom_button && pkg.custom_features ? (
                  pkg.custom_features.split(',').map((featureText, index) => (
                    <li key={index} className="flex items-center gap-1.5">
                      <span className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-blue-600 text-blue-600 bg-blue-50 text-[8px] font-bold shrink-0">✓</span>
                      <span className="text-slate-600 leading-tight">{featureText.trim()}</span>
                    </li>
                  ))
                ) : (
                  feats.length ? feats.map(f => (
                    <li key={f.code} className="flex items-center gap-1.5">
                      <span className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-blue-600 text-blue-600 bg-blue-50 text-[8px] font-bold shrink-0">✓</span>
                      <span className="text-slate-600 leading-tight">{f.name}</span>
                    </li>
                  )) : (
                    // Skeleton loading for features
                    <div className="space-y-1.5 animate-pulse">
                      <div className="h-2 w-3/4 bg-slate-100 rounded"></div>
                      <div className="h-2 w-1/2 bg-slate-100 rounded"></div>
                      <div className="h-2 w-2/3 bg-slate-100 rounded"></div>
                    </div>
                  )
                )}
              </ul>

              {pkg.is_custom_button ? (
                <a
                  href={pkg.button_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto w-full py-2 rounded-lg text-xs font-semibold tracking-wide shadow-sm bg-slate-800 text-white hover:bg-slate-900 flex items-center justify-center"
                >
                  {pkg.button_text || t('topup.contactUs')}
                </a>
              ) : (
                <button
                  onClick={() => openCheckoutModal(pkg)}
                  disabled={Number(pkg.price) === 0 && claimedPackageIds.includes(pkg.id)}
                  className={`mt-auto w-full py-2 rounded-lg text-xs font-semibold tracking-wide shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${Number(pkg.price) === 0
                    ? (claimedPackageIds.includes(pkg.id) ? "bg-slate-300 text-slate-500" : "bg-emerald-600 text-white hover:bg-emerald-700")
                    : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                >
                  {Number(pkg.price) === 0
                    ? (claimedPackageIds.includes(pkg.id) ? t('topup.claimed') : t('topup.claimFree'))
                    : t('topup.buyNow')}
                </button>
              )}
            </div>
          );
        }) : (
          <p className="text-center text-slate-400 col-span-full py-10 rounded-2xl border border-dashed border-slate-200 bg-white">{t('topup.noPackages')}</p>
        )}
      </div>


      {/* ============== MODAL ============== */}
      {
        isCheckoutOpen && selectedPackage && (
          <>
            <div className="fixed inset-0 z-40 bg-black/40" onClick={closeCheckoutModal} />
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
              {/* Mobile: bottom-sheet; Desktop: wide centered */}
              <div
                className="w-full h-[92vh] sm:h-[86vh] sm:max-w-3xl lg:max-w-5xl rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl overflow-hidden sm:mx-4 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header sticky */}
                <div className="px-5 pt-4 pb-3 border-b border-slate-200 bg-white sticky top-0 z-10 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">
                      {Number(selectedPackage.price) === 0 ? t('topup.modal.titleClaim') : t('topup.modal.titleConfirm')}
                    </h2>
                    <p className="text-xs text-slate-500">
                      {Number(selectedPackage.price) === 0 ? t('topup.modal.descClaim') : t('topup.modal.descConfirm')}
                    </p>
                  </div>
                  <button onClick={closeCheckoutModal} className="p-1 rounded-full hover:bg-slate-100 text-slate-500" aria-label={t('topup.modal.close')} disabled={isSubmitting}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                </div>

                {/* FREE PLAN UI */}
                {Number(selectedPackage.price) === 0 ? (
                  <div className="p-5 space-y-4">
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
                      <div className="text-emerald-600 font-bold text-lg mb-1">{t('topup.modal.freePrice')}</div>
                      <p className="text-sm text-emerald-800">{t('topup.modal.youGet')} <span className="font-bold">{selectedPackage.base_credits?.toLocaleString("id-ID")} Kredit</span>.</p>
                      <p className="text-xs text-emerald-600 mt-2 italic">{t('topup.modal.onlyOnce')}</p>
                    </div>
                    {checkoutError && <p className="text-sm text-rose-600 text-center bg-rose-50 p-2 rounded-lg border border-rose-100">{checkoutError}</p>}

                    <div className="flex gap-3 mt-4">
                      <button onClick={closeCheckoutModal} disabled={isSubmitting} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">
                        {t('topup.modal.cancel')}
                      </button>
                      <button onClick={handleConfirmPurchase} disabled={isSubmitting} className="flex-1 py-2.5 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 shadow-sm disabled:opacity-70">
                        {isSubmitting ? t('topup.modal.processing') : t('topup.modal.claimNow')}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* REGULAR PAYMENT UI */
                  <>
                    {/* Content: 2 columns on desktop */}
                    <div className="px-5 pb-4 pt-3 overflow-y-auto grow">
                      <div className="grid gap-4 lg:grid-cols-[340px,1fr]">
                        {/* LEFT column */}
                        <div className="space-y-3">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 space-y-1">
                            <p className="text-xs text-slate-500">{t('topup.modal.packageSelected')}</p>
                            <p className="text-sm font-semibold text-slate-900">{selectedPackage.name}</p>
                            <p className="text-xs text-slate-600">{selectedPackage.base_credits?.toLocaleString("id-ID")} kredit</p>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 space-y-1">
                            <p className="text-xs text-slate-500">{t('topup.modal.priceDetails')}</p>
                            {voucherInfo && voucherStatus === "valid" ? (
                              <>
                                <p className="text-xs text-slate-500 line-through">{rupiah(selectedPackage.price)}</p>
                                <p className="text-xs text-emerald-600">{t('topup.modal.voucherDiscount')} ({voucherInfo.discountRate}%): <span className="font-semibold">-{rupiah(voucherInfo.discountAmount)}</span></p>
                                <p className="text-sm font-semibold text-slate-900">{t('topup.modal.totalPay')}: <span className="text-blue-600">{rupiah(voucherInfo.finalPrice)}</span></p>
                              </>
                            ) : (
                              <p className="text-sm font-semibold text-slate-900">{t('topup.modal.totalPay')}: {rupiah(selectedPackage.price)}</p>
                            )}
                          </div>

                          <div className="space-y-1">
                            <label className="block text-xs font-medium text-slate-700">{t('topup.modal.voucherLabel')}</label>
                            <input
                              value={voucherCode}
                              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                              placeholder={t('topup.modal.voucherPlaceholder')}
                              disabled={isSubmitting}
                            />
                            <p className={`text-[11px] ${voucherStatus === "own_voucher" || voucherStatus === "invalid" ? "text-rose-500" : "text-slate-400"}`}>
                              {voucherStatus === "checking" ? t('topup.modal.checkingVoucher') :
                                voucherStatus === "valid" ? t('topup.modal.voucherValid', { code: voucherInfo?.voucher?.code }) :
                                  voucherStatus === "invalid" ? t('topup.modal.voucherInvalid') :
                                    voucherStatus === "own_voucher" ? t('topup.modal.voucherOwnError') :
                                      t('topup.modal.voucherHint')}
                            </p>
                          </div>
                        </div>

                        {/* RIGHT column */}
                        <div className="space-y-3">
                          {/* Filter chips */}
                          <div className="flex flex-wrap items-center gap-2">
                            {visibleGroups.map(g => (
                              <button key={g.key} onClick={() => setActiveGroup(g.key)}
                                className={"px-3 py-1.5 rounded-full border text-xs " +
                                  (activeGroup === g.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50")}
                                type="button"
                              >{t(`topup.filters.${g.labelKey}`)}</button>
                            ))}
                          </div>

                          {/* Channels list */}
                          <div className="max-h-[48vh] lg:max-h-[58vh] overflow-y-auto pr-1">
                            {channelsLoading ? (
                              <div className="space-y-2">{[...Array(8)].map((_, i) => (<div key={i} className="h-12 rounded-lg bg-slate-100 animate-pulse" />))}</div>
                            ) : filteredChannels.length === 0 ? (
                              <div className="text-[12px] text-rose-600">{t('topup.modal.noChannels')}</div>
                            ) : (
                              <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-2" role="radiogroup" aria-label="Metode pembayaran">
                                {filteredChannels.map(c => (
                                  <label key={c.code}
                                    className={"flex items-center gap-3 p-2 rounded-lg border min-h-[52px] transition cursor-pointer " +
                                      (selectedPayMethod === c.code ? "border-blue-600 ring-1 ring-blue-200 bg-blue-50/40" : "border-slate-200 hover:bg-slate-50")}
                                  >
                                    <input type="radio" name="paymethod" className="sr-only" value={c.code}
                                      checked={selectedPayMethod === c.code} onChange={() => setSelectedPayMethod(c.code)} disabled={isSubmitting} />
                                    {c.img && <img src={c.img} alt="" className="h-6 w-auto object-contain shrink-0" />}
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                                      <div className="text-[11px] text-slate-500 truncate">Kode: {c.code}{c.fee ? ` • ${t('topup.modal.fee')}: ${c.fee}` : ""}</div>
                                    </div>
                                    <span aria-hidden className={"h-4 w-4 rounded-full border flex items-center justify-center " +
                                      (selectedPayMethod === c.code ? "border-blue-600" : "border-slate-300")}
                                    >
                                      {selectedPayMethod === c.code && <span className="h-2.5 w-2.5 rounded-full bg-blue-600 block" />}
                                    </span>
                                  </label>
                                ))}
                              </fieldset>
                            )}
                          </div>

                          {checkoutError && <p className="text-[12px] text-rose-600">{checkoutError}</p>}
                        </div>
                      </div>
                    </div>

                    {/* Footer sticky */}
                    <div className="px-5 py-3 border-t border-slate-200 bg-white sticky bottom-0">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={closeCheckoutModal}
                          className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-xs sm:text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                          disabled={isSubmitting}
                        >{t('topup.modal.cancel')}</button>
                        <button onClick={handleConfirmPurchase}
                          className="px-4 py-2 rounded-lg bg-blue-600 text-xs sm:text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          disabled={isSubmitting || !selectedPayMethod}
                        >{isSubmitting ? t('topup.modal.redirecting') : t('topup.modal.payNow')}</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        )
      }
    </main >
  );
}
